// src/pages/ClientDashboardPage.tsx

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiDollarSign, FiTrendingUp, FiPieChart, FiArrowRight, FiInfo, FiCreditCard, FiCheckCircle, FiClock } from 'react-icons/fi';
import ProjectCard from '../components/common/ProjectCard/ProjectCard';
import styles from './ClientDashboardPage.module.css';
import { DashboardService } from '../services/dashboard.service';
import { PortfolioService } from '../services/portfolio.service';
import { InvestmentsService } from '../services/investments.service';
import { ProfileService, type KycMeSummary } from '../services/profile.service';
import { BankService, type BankDetails } from '../services/bank.service';
import type { Project, Investment } from '../types/Project';
import type { PortfolioResponse } from '../types/Portfolio';
import { computeAvailableFundsFromPositions, getClosedProjectIds, safeParseJson } from '../utils/availableFunds';
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import { DeveloperRequestsService, DeveloperAccessRequest } from '../services/developer-requests.service';
import { SystemService } from '../services/system.service';

interface DashboardData {
  totalInvested: number;
  activeInvestments: number;
  currentReturnPercent: number;
  featuredProjects: Project[];
  availableFunds: number;
  portfolio: PortfolioResponse | null;
  recentInvestments: Investment[];
  kycSummary: KycMeSummary | null;
  bankDetails: BankDetails | null;
}

const CLOSED_NOTIFIED_KEY = 'pf_closed_positions_notified_v1';

function formatDateTime(dt: Date, locale: string) {
  return dt.toLocaleString(locale, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const ClientDashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { addNotification } = useNotification();
  const { user, profileStatus } = useAuth();

  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [devRequest, setDevRequest] = useState<DeveloperAccessRequest | null>(null);
  const [devModalOpen, setDevModalOpen] = useState(false);
  const [devSubmitting, setDevSubmitting] = useState(false);
  const [developerRequestsEnabled, setDeveloperRequestsEnabled] = useState(true);

  const isSpanish = i18n.language.startsWith('es');
  const locale = isSpanish ? 'es-MX' : 'en-US';
  const currency = isSpanish ? 'MXN' : 'USD';

  const formatCurrency = (amount: number) =>
    Number(amount || 0).toLocaleString(locale, { style: 'currency', currency, minimumFractionDigits: 0 });

  const notifyNewClosedPositions = (portfolio: PortfolioResponse) => {
    const ids = getClosedProjectIds(portfolio.positions || []);
    const prev = safeParseJson<number[]>(localStorage.getItem(CLOSED_NOTIFIED_KEY)) || [];
    const prevSet = new Set(prev);

    const newIds = ids.filter(id => !prevSet.has(id));
    if (newIds.length === 0) return;

    const breakdown = computeAvailableFundsFromPositions(portfolio.positions || []);
    const now = new Date();

    const message =
      `${t('investments.closure_notification_title') || 'Fondos disponibles por inversión cerrada'}\n` +
      `${t('common.date') || 'Fecha'}: ${formatDateTime(now, locale)}\n` +
      `${t('portfolio.available_breakdown_capital') || 'Capital'}: ${formatCurrency(breakdown.capital)}\n` +
      `${t('portfolio.available_breakdown_return') || 'Rendimiento'}: ${formatCurrency(breakdown.rendimiento)}\n` +
      `${t('portfolio.available_breakdown_total') || 'Total disponible'}: ${formatCurrency(breakdown.total)}\n` +
      `${t('portfolio.available_hint') || 'Puedes consultar el detalle en Portafolio.'}`;

    addNotification(message, 'success');
    localStorage.setItem(CLOSED_NOTIFIED_KEY, JSON.stringify(ids));
  };

  const formatShortDate = (value?: string | Date | null) => {
    if (!value) return '—';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(locale, { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);

    Promise.allSettled([
      DashboardService.getClientDashboard(),
      PortfolioService.getMyPortfolio(),
      InvestmentsService.getMyInvestments(),
      ProfileService.getKycSummary(),
      BankService.getMyBankDetails(),
    ])
      .then((results) => {
        if (!alive) return;

        const dashboardRes = results[0].status === 'fulfilled' ? results[0].value : null;
        const portfolio = results[1].status === 'fulfilled' ? results[1].value : null;
        const investments = results[2].status === 'fulfilled' && Array.isArray(results[2].value) ? results[2].value : [];
        const kycSummary = results[3].status === 'fulfilled' ? results[3].value : null;
        const bankDetails = results[4].status === 'fulfilled' ? results[4].value : null;

        if (!dashboardRes) {
          setData(null);
          return;
        }

        const availableFunds =
          portfolio && portfolio.positions ? computeAvailableFundsFromPositions(portfolio.positions).total : 0;

        const recentInvestments = [...investments].sort((a, b) => {
          const da = new Date(a.date || 0).getTime();
          const db = new Date(b.date || 0).getTime();
          return db - da;
        });

        setData({
          totalInvested: dashboardRes.totalInvested ?? 0,
          activeInvestments: dashboardRes.activeInvestments ?? 0,
          currentReturnPercent: dashboardRes.currentReturnPercent ?? 0,
          featuredProjects: Array.isArray(dashboardRes.featuredProjects) ? dashboardRes.featuredProjects : [],
          availableFunds,
          portfolio,
          recentInvestments,
          kycSummary,
          bankDetails,
        });

        if (portfolio) {
          notifyNewClosedPositions(portfolio);
        }
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [t, locale, currency]);

  useEffect(() => {
    let alive = true;
    if (user?.role !== 'client') return;
    if (profileStatus?.user_status && profileStatus.user_status !== 'ACTIVE') return;

    SystemService.getFlag('developer_requests_enabled')
      .then((enabled) => {
        if (!alive) return;
        setDeveloperRequestsEnabled(!!enabled);
      })
      .catch(() => {
        if (!alive) return;
        setDeveloperRequestsEnabled(true);
      });

    DeveloperRequestsService.getMyRequest()
      .then((res) => {
        if (!alive) return;
        setDevRequest(res);
      })
      .catch(() => {
        if (!alive) return;
        setDevRequest(null);
      });

    return () => {
      alive = false;
    };
  }, [user?.role, profileStatus?.user_status]);

  const totalInvested = data?.totalInvested ?? 0;
  const activeInvestments = data?.activeInvestments ?? 0;
  const currentReturnPercent = data?.currentReturnPercent ?? 0;
  const availableFunds = data?.availableFunds ?? 0;

  const featuredProjects = useMemo(() => data?.featuredProjects ?? [], [data]);

  const portfolioQuick = useMemo(() => {
    const positions = data?.portfolio?.positions ?? [];
    const active = positions.filter((p) => p.status === 'active').length;
    const closed = positions.filter((p) => p.status === 'closed').length;
    const pending = positions.filter((p) => p.status === 'pending').length;
    const topProjects = [...positions]
      .sort((a, b) => Number(b.investedAmount || 0) - Number(a.investedAmount || 0))
      .slice(0, 3);
    return { active, closed, pending, topProjects };
  }, [data?.portfolio]);

  const recentMovements = useMemo(() => (data?.recentInvestments ?? []).slice(0, 5), [data?.recentInvestments]);

  const isActiveUser = profileStatus?.user_status ? profileStatus.user_status === 'ACTIVE' : true;
  const canRequestDeveloper =
    user?.role === 'client' &&
    isActiveUser &&
    developerRequestsEnabled &&
    (!devRequest || devRequest.status !== 'PENDING');

  const isPendingDeveloper = devRequest?.status === 'PENDING';

  const hasBankClabe = Boolean(data?.bankDetails?.clabe);
  const kycStatus = data?.kycSummary?.kycStatus || 'not_started';
  const pldStatus = data?.kycSummary?.pldStatus || 'not_started';
  const statusLabel = (status: string) =>
    (
      {
        not_started: 'Sin Empezar',
        pending: 'Pendiente',
        approved: 'Aprobado',
        rejected: 'Rechazado',
      } as Record<string, string>
    )[status] || status;

  const alertItems = useMemo(() => {
    const items: string[] = [];
    if (!profileStatus?.profile_completed) items.push('Completa tu perfil para habilitar más operaciones.');
    if (!hasBankClabe) items.push('Registra una CLABE para retiros y devoluciones de fondos.');
    if (kycStatus !== 'approved' || pldStatus !== 'approved') items.push('Tus documentos/KYC siguen pendientes de aprobación.');
    if (isPendingDeveloper) items.push('Tu solicitud para acceso de desarrollador está en revisión.');
    if ((data?.portfolio?.positions || []).some((p) => p.status === 'active')) {
      items.push('El rendimiento de proyectos activos se calcula al finalizar el proyecto.');
    }
    return items;
  }, [profileStatus?.profile_completed, hasBankClabe, kycStatus, pldStatus, isPendingDeveloper, data?.portfolio?.positions]);

  const actionItems = useMemo(() => {
    return [
      {
        key: 'complete-profile',
        title: 'Completar perfil',
        description: profileStatus?.profile_completed ? 'Perfil completo' : 'Completa tus datos para habilitar operaciones',
        to: '/complete-profile',
        status: profileStatus?.profile_completed ? 'done' : 'pending',
      },
      {
        key: 'kyc-docs',
        title: 'Agregar documentos personales',
        description:
          kycStatus === 'approved' && pldStatus === 'approved'
            ? 'KYC / PLD aprobados'
            : `KYC: ${statusLabel(kycStatus)} · PLD: ${statusLabel(pldStatus)}`,
        to: '/perfil',
        status: kycStatus === 'approved' && pldStatus === 'approved' ? 'done' : 'pending',
      },
      {
        key: 'clabe',
        title: 'Registrar / verificar CLABE',
        description: hasBankClabe ? `CLABE registrada en ${data?.bankDetails?.bank_name || 'tu banco'}` : 'Configura tu cuenta bancaria',
        to: '/perfil',
        status: hasBankClabe ? 'done' : 'pending',
      },
      {
        key: 'statement',
        title: 'Descargar estado de cuenta',
        description: 'Genera PDF o CSV desde Portafolio / Reportes',
        to: '/reportes',
        status: 'cta',
      },
      {
        key: 'projects',
        title: 'Ver proyectos disponibles',
        description: 'Explora nuevas oportunidades de inversión',
        to: '/proyectos',
        status: 'cta',
      },
    ] as const;
  }, [profileStatus?.profile_completed, kycStatus, pldStatus, hasBankClabe, data?.bankDetails?.bank_name]);

  const submitDeveloperRequest = async () => {
    setDevSubmitting(true);
    try {
      const res = await DeveloperRequestsService.requestAccess();
      setDevRequest(res);
      addNotification(t('developer_request.request_sent'), 'success');
      setDevModalOpen(false);
    } catch (error: any) {
      addNotification(error?.message || t('developer_request.request_error'), 'error');
    } finally {
      setDevSubmitting(false);
    }
  };

  if (loading) return <div className={styles.loading}>{t('common.loading') ?? 'Cargando...'}</div>;

  return (
    <div className={styles.dashboardContainer}>
      <header className={styles.pageHeader}>
        <div className={styles.headerText}>
          <h1 className={styles.title}>{t('common.dashboard')}</h1>
          <p className={styles.subtitle}>{t('dashboard.overview_subtitle')}</p>
        </div>

        <Link to="/proyectos" className={styles.ctaButton}>
          {t('public.explore_projects', 'Explorar Proyectos')} <FiArrowRight />
        </Link>
      </header>

      {(canRequestDeveloper || isPendingDeveloper) && (
        <section className={styles.panel} style={{ marginBottom: '24px' }}>
          <div className={styles.sectionHeader} style={{ marginBottom: '12px' }}>
            <h2 className={styles.sectionTitle}>{t('developer_request.cta_title')}</h2>
          </div>
          <p className={styles.subtitle}>
            {isPendingDeveloper ? t('developer_request.pending_message') : t('developer_request.cta_subtitle')}
          </p>
          {canRequestDeveloper && (
            <button className={styles.ctaButton} onClick={() => setDevModalOpen(true)}>
              {t('developer_request.cta_button')}
            </button>
          )}
        </section>
      )}

      <section className={styles.panel}>
        <div className={styles.metricsGrid}>
          <div className={styles.metricCard}>
            <div className={`${styles.iconBox} ${styles.blue}`}>
              <FiDollarSign />
            </div>
            <div className={styles.metricContent}>
              <span className={styles.metricLabel}>{t('dashboard.total_invested')}</span>
              <h3 className={styles.metricValue}>{formatCurrency(totalInvested)}</h3>
            </div>
          </div>

          <div className={styles.metricCard}>
            <div className={`${styles.iconBox} ${styles.green}`}>
              <FiTrendingUp />
            </div>
            <div className={styles.metricContent}>
              <span className={styles.metricLabel}>{t('dashboard.current_return_label')}</span>
              <h3 className={styles.metricValue}>+{currentReturnPercent}%</h3>
            </div>
          </div>

          <div className={styles.metricCard}>
            <div className={`${styles.iconBox} ${styles.purple}`}>
              <FiPieChart />
            </div>
            <div className={styles.metricContent}>
              <span className={styles.metricLabel}>{t('dashboard.active_investments')}</span>
              <h3 className={styles.metricValue}>{activeInvestments}</h3>
            </div>
          </div>

          <div className={styles.metricCard}>
            <div className={`${styles.iconBox} ${styles.blue}`}>
              <FiCreditCard />
            </div>
            <div className={styles.metricContent}>
              <span className={styles.metricLabel}>{t('portfolio.available_funds') || 'Fondos disponibles'}</span>
              <h3 className={styles.metricValue}>{formatCurrency(availableFunds)}</h3>
            </div>
          </div>
        </div>

        <div className={styles.sectionGrid}>
          <section className={styles.subPanel}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Acciones recomendadas</h2>
            </div>
            <div className={styles.actionGrid}>
              {actionItems.map((item) => (
                <Link key={item.key} to={item.to} className={styles.actionCard}>
                  <div className={styles.actionCardHead}>
                    <span className={styles.actionCardTitle}>{item.title}</span>
                    <span
                      className={`${styles.actionBadge} ${
                        item.status === 'done' ? styles.actionBadgeDone : item.status === 'pending' ? styles.actionBadgePending : styles.actionBadgeCta
                      }`}
                    >
                      {item.status === 'done' ? <FiCheckCircle /> : item.status === 'pending' ? <FiClock /> : <FiArrowRight />}
                      {item.status === 'done' ? 'Listo' : item.status === 'pending' ? 'Pendiente' : 'Ir'}
                    </span>
                  </div>
                  <p className={styles.actionCardText}>{item.description}</p>
                </Link>
              ))}
            </div>
          </section>

          <section className={styles.subPanel}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Mi portafolio rápido</h2>
            </div>
            <div className={styles.quickPortfolioGrid}>
              <div className={styles.quickMetric}>
                <span>Activas</span>
                <strong>{portfolioQuick.active}</strong>
              </div>
              <div className={styles.quickMetric}>
                <span>Finalizadas</span>
                <strong>{portfolioQuick.closed}</strong>
              </div>
              <div className={styles.quickMetric}>
                <span>Pendientes</span>
                <strong>{portfolioQuick.pending}</strong>
              </div>
            </div>
            <div className={styles.miniList}>
              {portfolioQuick.topProjects.length === 0 ? (
                <div className={styles.emptyLine}>Aún no tienes posiciones en portafolio.</div>
              ) : (
                portfolioQuick.topProjects.map((p) => (
                  <div key={p.projectId} className={styles.miniListItem}>
                    <div>
                      <div className={styles.miniListTitle}>{p.projectTitle}</div>
                      <div className={styles.miniListMeta}>{p.status === 'closed' ? 'Finalizado' : p.status === 'active' ? 'Activo' : 'Pendiente'}</div>
                    </div>
                    <div className={styles.miniListAmount}>{formatCurrency(Number(p.investedAmount || 0))}</div>
                  </div>
                ))
              )}
            </div>
            {portfolioQuick.active > 0 ? (
              <div className={styles.hintBanner}>El rendimiento se calcula al finalizar el proyecto para posiciones activas.</div>
            ) : null}
          </section>
        </div>

        <div className={styles.sectionGrid}>
          <section className={styles.subPanel}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Movimientos recientes</h2>
              <Link to="/mis-inversiones" className={styles.inlineLink}>Ver todas</Link>
            </div>
            <div className={styles.miniList}>
              {recentMovements.length === 0 ? (
                <div className={styles.emptyLine}>Aún no hay inversiones registradas.</div>
              ) : (
                recentMovements.map((inv) => (
                  <div key={inv.id} className={styles.miniListItem}>
                    <div>
                      <div className={styles.miniListTitle}>{inv.projectTitle}</div>
                      <div className={styles.miniListMeta}>
                        {formatShortDate(inv.date)} · {inv.status}
                        {String(inv.status).toLowerCase().includes('final') ? ` · Retorno ${formatCurrency(Number(inv.expectedReturn || 0))}` : ''}
                      </div>
                    </div>
                    <div className={styles.miniListAmount}>{formatCurrency(Number(inv.investedAmount || 0))}</div>
                  </div>
                ))
              )}
            </div>
          </section>

          <section className={styles.subPanel}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Alertas</h2>
            </div>
            <div className={styles.alertList}>
              {alertItems.length === 0 ? (
                <div className={styles.alertOk}>
                  <FiCheckCircle />
                  <span>No hay alertas pendientes. Tu panel está al día.</span>
                </div>
              ) : (
                alertItems.map((msg, idx) => (
                  <div key={`${idx}-${msg.slice(0, 10)}`} className={styles.alertItem}>
                    <FiInfo />
                    <span>{msg}</span>
                  </div>
                ))
              )}
            </div>
          </section>
        </div>

        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>{t('dashboard.featured_opportunities')}</h2>
        </div>

        {featuredProjects.length === 0 ? (
          <div className={styles.emptyState} role="status" aria-live="polite">
            <div className={styles.emptyIcon} aria-hidden="true">
              <FiInfo />
            </div>
            <div className={styles.emptyText}>
              <div className={styles.emptyTitle}>{t('projects.no_projects')}</div>
              <Link to="/proyectos" className={styles.emptyLink}>
                {t('public.explore_projects', 'Explorar Proyectos')} <FiArrowRight />
              </Link>
            </div>
          </div>
        ) : (
          <div className={styles.projectsGrid}>
            {featuredProjects.map(project => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        )}
      </section>

      {devModalOpen && (
        <div className={styles.modalOverlay} onClick={() => !devSubmitting && setDevModalOpen(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 className={styles.modalTitle}>{t('developer_request.modal_title')}</h2>
            <p className={styles.modalText}>{t('developer_request.modal_body')}</p>

            <div className={styles.modalActions}>
              <button
                className={styles.modalCancel}
                onClick={() => setDevModalOpen(false)}
                disabled={devSubmitting}
              >
                {t('common.cancel')}
              </button>
              <button
                className={styles.modalConfirm}
                onClick={submitDeveloperRequest}
                disabled={devSubmitting}
              >
                {devSubmitting ? t('common.processing') : t('developer_request.modal_confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDashboardPage;
